import React, { useState, useEffect, useCallback } from 'react';
import TabNavigation from '../TabNavigation';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../contexts/NavigationBackContext';
import { useVessel } from '../../../contexts/VesselContext';
import { upsert, filterByVessel } from '../../../lib/collections';
import { genReviewId, genReportId, genTimestamp } from '../../../lib/id';
import { readJSON } from '../../../lib/storageRouter';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: exclude soft-deleted records from compliance reports
import { getDrillRequirements } from '../../../config/drillRequirements';
import { SMSReviewsContent } from './reports/SMSReviews';
import { ComplianceReportsContent } from './reports/ComplianceReports';
import { TrendsAnalyticsContent } from './reports/TrendsAnalytics';
import { SMSReviewModal, ReportViewModal } from './reports/ReportModals';

const SMS_REVIEWS_KEY = 'sr.sms_reviews.v1';
const REPORTS_KEY = 'sr.reports.v1';

const ReportsModule = ({ setActiveModule }) => {
  // FT-354a: return to dashboard when a child tab's mobile back button fires.
  const handleBack = setActiveModule ? () => setActiveModule('dashboard') : undefined;

  // FT-357: also register the same handler at the module level via the
  // NavigationBackContext so mobile users always see a Back button in the
  // bottom bar, regardless of which sub-tab is active.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  const { currentVesselId, getCurrentVessel } = useVessel();
  const [activeTab, setActiveTab] = useState('sms');
  const [smsReviews, setSmsReviews] = useState([]);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [savedReports, setSavedReports] = useState([]);

  const tabs = [
    { id: 'sms', label: 'SMS Reviews' },
    { id: 'compliance', label: 'Compliance Reports' },
    { id: 'trends', label: 'Trends/Analytics' }
  ];

  // Load SMS reviews and saved reports for current vessel
  useEffect(() => {
    if (currentVesselId) {
      (async () => {
        const vesselReviews = await filterByVessel(SMS_REVIEWS_KEY, currentVesselId);
        setSmsReviews(vesselReviews);

        // Load saved reports
        const vesselReports = await filterByVessel(REPORTS_KEY, currentVesselId);
        setSavedReports(vesselReports);
      })();
    }
  }, [currentVesselId]);

  // Gather compliance data from all modules
  useEffect(() => {
    if (currentVesselId) {
      gatherComplianceData();
    }
  }, [currentVesselId, activeTab]);

  const gatherComplianceData = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentVessel = getCurrentVessel();

    const data = {
      drills: {
        total: 0,
        completed: 0,
        overdue: 0,
        dueSoon: 0,
        records: [],
        overdueItems: [],
        dueSoonItems: []
      },
      equipment: {
        total: 0,
        checked: 0,
        overdue: 0,
        dueSoon: 0,
        records: [],
        overdueItems: [],
        dueSoonItems: []
      },
      crew: {
        total: 0,
        compliant: 0,
        certificates: {
          total: 0,
          expired: 0,
          expiringSoon: 0,
          expiredItems: [],
          expiringSoonItems: []
        },
        familiarisation: {
          total: 0,
          completed: 0,
          incomplete: []
        },
        hoursOfRest: {
          compliant: 0,
          violations: 0,
          violationItems: []
        }
      },
      vessel: {
        certificates: {
          total: 0,
          expired: 0,
          expiringSoon: 0,
          expiredItems: [],
          expiringSoonItems: []
        }
      },
      defects: {
        total: 0,
        open: 0,
        critical: 0,
        overdue: 0,
        overdueItems: []
      },
      documents: {
        total: 0,
        active: 0,
        overdue: 0,
        dueSoon: 0,
        overdueItems: [],
        dueSoonItems: []
      },
      incidents: {
        total: 0,
        open: 0,
        year: 0
      },
      // Summary for quick access
      criticalAlerts: [],
      warnings: []
    };

    // === DRILLS COMPLIANCE ===
    // Get drill requirements for current vessel
    if (currentVessel) {
      const drillRequirements = getDrillRequirements(currentVessel);
      const drillsKey = `drills_${currentVesselId}`;
      const drills = filterTombstoned(await readJSON(drillsKey, []));  // FT-458: exclude soft-deleted drills from the report
      data.drills.records = drills;

      // Calculate compliance for each required drill type
      Object.entries(drillRequirements).forEach(([drillType, requirement]) => {
        if (requirement.type === 'info') return; // Skip info entries

        data.drills.total++;

        // Find most recent drill of this type
        const recentDrill = drills
          .filter(d => d.type === drillType || d.drill_type === drillType)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        let nextDue;
        if (recentDrill) {
          const drillDate = new Date(recentDrill.date);
          nextDue = new Date(drillDate.getTime() + (requirement.frequency_days * 24 * 60 * 60 * 1000));
          data.drills.completed++;
        } else {
          // No drill recorded - overdue immediately
          nextDue = new Date(today);
          nextDue.setDate(nextDue.getDate() - 1); // Set to yesterday to mark overdue
        }

        const daysUntilDue = Math.floor((nextDue - today) / (1000 * 60 * 60 * 24));
        const drillName = requirement.customName || drillType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        if (daysUntilDue < 0) {
          data.drills.overdue++;
          const item = {
            type: 'drill',
            name: drillName,
            drillType,
            daysOverdue: Math.abs(daysUntilDue),
            lastCompleted: recentDrill?.date || null,
            requirement: requirement.compliance_level,
            regulatory: requirement.regulatory_source
          };
          data.drills.overdueItems.push(item);

          // Critical alert for required drills
          if (requirement.compliance_level === 'Required') {
            data.criticalAlerts.push({
              type: 'drill_overdue',
              severity: 'critical',
              title: `${drillName} Drill Overdue`,
              message: `Required drill is ${Math.abs(daysUntilDue)} days overdue`,
              regulatory: requirement.regulatory_source,
              action: 'Record drill immediately'
            });
          }
        } else if (daysUntilDue <= 7) {
          data.drills.dueSoon++;
          data.drills.dueSoonItems.push({
            type: 'drill',
            name: drillName,
            drillType,
            daysUntilDue,
            lastCompleted: recentDrill?.date || null,
            requirement: requirement.compliance_level
          });

          // Warning for required drills due within 7 days
          if (requirement.compliance_level === 'Required' && daysUntilDue <= 3) {
            data.warnings.push({
              type: 'drill_due_soon',
              severity: 'warning',
              title: `${drillName} Drill Due Soon`,
              message: `Due in ${daysUntilDue} days`,
              action: 'Schedule drill'
            });
          }
        }
      });
    }

    // === EQUIPMENT COMPLIANCE ===
    const equipmentKey = `equipmentChecks_${currentVesselId}`;
    const equipmentChecks = await readJSON(equipmentKey, []);

    // LSA and FFE equipment tracking
    const equipmentTypes = [
      { type: 'lsa', name: 'Life Saving Appliances', frequencyDays: 30 },
      { type: 'ffe', name: 'Fire Fighting Equipment', frequencyDays: 30 }
    ];

    equipmentTypes.forEach(({ type, name, frequencyDays }) => {
      data.equipment.total++;

      const recentCheck = equipmentChecks
        .filter(check => check.type === type)
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      if (recentCheck) {
        data.equipment.checked++;
        const checkDate = new Date(recentCheck.date);
        const nextDue = new Date(checkDate);
        nextDue.setDate(nextDue.getDate() + frequencyDays);

        const daysUntilDue = Math.floor((nextDue - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue < 0) {
          data.equipment.overdue++;
          const item = {
            type: 'equipment',
            name,
            equipmentType: type,
            daysOverdue: Math.abs(daysUntilDue),
            lastChecked: recentCheck.date
          };
          data.equipment.overdueItems.push(item);
          data.criticalAlerts.push({
            type: 'equipment_overdue',
            severity: 'critical',
            title: `${name} Check Overdue`,
            message: `Monthly check is ${Math.abs(daysUntilDue)} days overdue`,
            action: 'Complete equipment check'
          });
        } else if (daysUntilDue <= 7) {
          data.equipment.dueSoon++;
          data.equipment.dueSoonItems.push({
            type: 'equipment',
            name,
            equipmentType: type,
            daysUntilDue,
            lastChecked: recentCheck.date
          });
        }
      } else {
        // No check recorded - overdue
        data.equipment.overdue++;
        data.equipment.overdueItems.push({
          type: 'equipment',
          name,
          equipmentType: type,
          daysOverdue: 'Never checked',
          lastChecked: null
        });
        data.criticalAlerts.push({
          type: 'equipment_never_checked',
          severity: 'critical',
          title: `${name} Never Checked`,
          message: 'No equipment check on record',
          action: 'Complete initial equipment check'
        });
      }
    });
    data.equipment.records = equipmentChecks;

    // === CREW COMPLIANCE ===
    // Use vessel-scoped key and case-insensitive filter
    const crewKey = currentVesselId ? `crew_${currentVesselId}` : 'crew';
    const crew = (await readJSON(crewKey, [])).filter(m => {
      const status = (m.status || 'active').toLowerCase();
      return status === 'active';
    });
    data.crew.total = crew.length;
    let crewCompliant = 0;

    crew.forEach(member => {
      let memberCompliant = true;
      const memberName = member.full_name || member.name || 'Unknown';

      // Certificate compliance
      if (member.certificates && member.certificates.length > 0) {
        member.certificates.forEach(cert => {
          data.crew.certificates.total++;
          const expiryDate = new Date(cert.expiryDate || cert.expiry_date);
          const daysToExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

          if (daysToExpiry < 0) {
            data.crew.certificates.expired++;
            memberCompliant = false;
            const item = {
              crewMember: memberName,
              certificate: cert.name || cert.type,
              daysExpired: Math.abs(daysToExpiry),
              expiryDate: cert.expiryDate || cert.expiry_date
            };
            data.crew.certificates.expiredItems.push(item);
            data.criticalAlerts.push({
              type: 'certificate_expired',
              severity: 'critical',
              title: `Expired Certificate: ${cert.name || cert.type}`,
              message: `${memberName}'s certificate expired ${Math.abs(daysToExpiry)} days ago`,
              action: 'Renew certificate or remove from active duty'
            });
          } else if (daysToExpiry <= 30) {
            data.crew.certificates.expiringSoon++;
            data.crew.certificates.expiringSoonItems.push({
              crewMember: memberName,
              certificate: cert.name || cert.type,
              daysToExpiry,
              expiryDate: cert.expiryDate || cert.expiry_date
            });
            if (daysToExpiry <= 7) {
              data.warnings.push({
                type: 'certificate_expiring',
                severity: 'warning',
                title: `Certificate Expiring: ${cert.name || cert.type}`,
                message: `${memberName}'s certificate expires in ${daysToExpiry} days`,
                action: 'Schedule renewal'
              });
            }
          }
        });
      }

      // Familiarisation tracking
      data.crew.familiarisation.total++;
      const isFamiliarised = member.familiarisation_complete || member.familiarisationComplete;
      if (isFamiliarised) {
        data.crew.familiarisation.completed++;
      } else {
        memberCompliant = false;
        data.crew.familiarisation.incomplete.push({
          crewMember: memberName,
          joinDate: member.joinDate || member.join_date,
          status: member.status
        });
      }

      if (memberCompliant) crewCompliant++;
    });
    data.crew.compliant = crewCompliant;

    // === HOURS OF REST COMPLIANCE ===
    const horKey = `sr.hours_of_rest.v1`;
    const horRecords = await filterByVessel(horKey, currentVesselId);

    // Check last 7 days for violations
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentHoR = horRecords.filter(r => new Date(r.date) >= sevenDaysAgo);
    recentHoR.forEach(record => {
      if (record.violation || record.hasViolation) {
        data.crew.hoursOfRest.violations++;
        data.crew.hoursOfRest.violationItems.push({
          crewMember: record.crewName || record.crew_name,
          date: record.date,
          violationType: record.violationType || 'Rest period violation'
        });
      } else {
        data.crew.hoursOfRest.compliant++;
      }
    });

    // Add HoR violations to warnings
    if (data.crew.hoursOfRest.violations > 0) {
      data.warnings.push({
        type: 'hor_violations',
        severity: 'warning',
        title: 'Hours of Rest Violations',
        message: `${data.crew.hoursOfRest.violations} violation(s) in the last 7 days`,
        action: 'Review rest schedules'
      });
    }

    // === VESSEL CERTIFICATES ===
    const vesselCertsKey = `sr.vessel_certificates.v1.${currentVesselId}`;
    const vesselCerts = await readJSON(vesselCertsKey, []);

    vesselCerts.forEach(cert => {
      data.vessel.certificates.total++;
      const expiryDate = new Date(cert.expiryDate || cert.expiry_date);
      const daysToExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

      if (daysToExpiry < 0) {
        data.vessel.certificates.expired++;
        data.vessel.certificates.expiredItems.push({
          certificate: cert.name || cert.type,
          daysExpired: Math.abs(daysToExpiry),
          expiryDate: cert.expiryDate || cert.expiry_date
        });
        data.criticalAlerts.push({
          type: 'vessel_certificate_expired',
          severity: 'critical',
          title: `Vessel Certificate Expired: ${cert.name || cert.type}`,
          message: `Certificate expired ${Math.abs(daysToExpiry)} days ago - DETENTION RISK`,
          action: 'Renew immediately'
        });
      } else if (daysToExpiry <= 90) {
        data.vessel.certificates.expiringSoon++;
        data.vessel.certificates.expiringSoonItems.push({
          certificate: cert.name || cert.type,
          daysToExpiry,
          expiryDate: cert.expiryDate || cert.expiry_date
        });
        if (daysToExpiry <= 30) {
          data.warnings.push({
            type: 'vessel_certificate_expiring',
            severity: 'warning',
            title: `Vessel Certificate Expiring: ${cert.name || cert.type}`,
            message: `Expires in ${daysToExpiry} days`,
            action: 'Schedule renewal'
          });
        }
      }
    });

    // === DEFECTS COMPLIANCE ===
    const defectsKey = `defects_${currentVesselId}`;
    const defects = filterTombstoned(await readJSON(defectsKey, []));  // FT-458: exclude soft-deleted defects from the report
    data.defects.total = defects.length;
    data.defects.open = defects.filter(d => d.status === 'open' || d.status === 'in_progress').length;
    data.defects.critical = defects.filter(d => d.priority === 'critical' || d.severity === 'critical').length;

    // Check for overdue defects (past target date)
    defects.forEach(defect => {
      if ((defect.status === 'open' || defect.status === 'in_progress') && defect.targetDate) {
        const targetDate = new Date(defect.targetDate);
        if (targetDate < today) {
          data.defects.overdue++;
          data.defects.overdueItems.push({
            id: defect.id,
            description: defect.description || defect.title,
            priority: defect.priority,
            daysOverdue: Math.floor((today - targetDate) / (1000 * 60 * 60 * 24)),
            targetDate: defect.targetDate
          });

          if (defect.priority === 'critical' || defect.severity === 'critical') {
            data.criticalAlerts.push({
              type: 'defect_overdue',
              severity: 'critical',
              title: 'Critical Defect Overdue',
              message: defect.description || defect.title,
              action: 'Complete rectification immediately'
            });
          }
        }
      }
    });

    // === DOCUMENTS/SMS COMPLIANCE ===
    // New approach: Read from sms_documents config (checklist-based)
    const smsDocsKey = `sms_documents_${currentVesselId}`;
    const smsDocsConfig = await readJSON(smsDocsKey, null);

    if (smsDocsConfig && smsDocsConfig.documentChecklist) {
      const checklist = smsDocsConfig.documentChecklist;
      const items = Object.values(checklist);
      data.documents.total = items.length;
      data.documents.active = items.filter(item => item.hasDocument).length;

      // Check for documents needing review
      items.forEach(item => {
        if (item.hasDocument && item.reviewDate) {
          const dueDate = new Date(item.reviewDate);
          const daysUntilReview = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));

          if (daysUntilReview < 0) {
            data.documents.overdue++;
            data.documents.overdueItems.push({
              name: item.label || item.docType,
              daysOverdue: Math.abs(daysUntilReview),
              reviewDate: item.reviewDate
            });
            data.warnings.push({
              type: 'document_review_overdue',
              severity: 'warning',
              title: `Document Review Overdue: ${item.label || item.docType}`,
              message: `Review was due ${Math.abs(daysUntilReview)} days ago`,
              action: 'Complete document review'
            });
          } else if (daysUntilReview <= 30) {
            data.documents.dueSoon++;
            data.documents.dueSoonItems.push({
              name: item.label || item.docType,
              daysUntilReview,
              reviewDate: item.reviewDate
            });
          }
        }
      });
    } else {
      // Fallback: Try legacy storage format for migration period
      const legacyDocs = await filterByVessel('sr.policies.v1', currentVesselId);
      data.documents.total = legacyDocs.length;
      data.documents.active = legacyDocs.filter(d => d.status === 'active').length;

      legacyDocs.forEach(doc => {
        const reviewDate = doc.reviewDate || doc.review_date || doc.nextReview;
        if (reviewDate) {
          const dueDate = new Date(reviewDate);
          const daysUntilReview = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));

          if (daysUntilReview < 0) {
            data.documents.overdue++;
            data.documents.overdueItems.push({
              name: doc.name || doc.title,
              daysOverdue: Math.abs(daysUntilReview),
              reviewDate
            });
            data.warnings.push({
              type: 'document_review_overdue',
              severity: 'warning',
              title: `Document Review Overdue: ${doc.name || doc.title}`,
              message: `Review was due ${Math.abs(daysUntilReview)} days ago`,
              action: 'Complete document review'
            });
          } else if (daysUntilReview <= 30) {
            data.documents.dueSoon++;
            data.documents.dueSoonItems.push({
              name: doc.name || doc.title,
              daysUntilReview,
              reviewDate
            });
          }
        }
      });
    }

    // === INCIDENTS ===
    const incidentsKey = `incidents_${currentVesselId}`;
    const incidents = filterTombstoned(await readJSON(incidentsKey, []));  // FT-458: exclude soft-deleted incidents from the report
    data.incidents.total = incidents.length;
    data.incidents.open = incidents.filter(i => i.status === 'open' || i.status === 'investigating').length;

    // Count incidents this year
    const yearStart = new Date(today.getFullYear(), 0, 1);
    data.incidents.year = incidents.filter(i => new Date(i.date) >= yearStart).length;

    setComplianceData(data);
  };

  // Handle SMS review save
  const handleSaveReview = async (reviewData) => {
    const record = {
      ...reviewData,
      id: reviewData.id || genReviewId(),
      vesselId: currentVesselId,
      status: 'completed',
      nextDue: calculateNextReviewDate(reviewData.reviewDate)
    };

    await upsert(SMS_REVIEWS_KEY, record);

    // Reload reviews
    const vesselReviews = await filterByVessel(SMS_REVIEWS_KEY, currentVesselId);
    setSmsReviews(vesselReviews);

    setShowAddReviewModal(false);
    setEditingReview(null);
  };

  // Calculate next review date (3 years)
  const calculateNextReviewDate = (reviewDate) => {
    const date = new Date(reviewDate);
    date.setFullYear(date.getFullYear() + 3);
    return date.toISOString();
  };

  // Generate and save compliance report
  const generateComplianceReport = async () => {
    const report = {
      id: genReportId('COMP'),
      vesselId: currentVesselId,
      type: 'compliance',
      generatedAt: genTimestamp(),
      data: complianceData,
      vessel: getCurrentVessel()
    };

    // Save report to collection
    await upsert(REPORTS_KEY, report);

    // Reload saved reports
    const vesselReports = await filterByVessel(REPORTS_KEY, currentVesselId);
    setSavedReports(vesselReports);

    setViewingReport(report);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sms':
        // FT-354a: onBack renders mobile BackButton (returns to dashboard).
        return <SMSReviewsContent
          reviews={smsReviews}
          onAddReview={() => setShowAddReviewModal(true)}
          onEditReview={setEditingReview}
          onBack={handleBack}
        />;
      case 'compliance':
        // FT-354a: onBack renders mobile BackButton (returns to dashboard).
        return <ComplianceReportsContent
          data={complianceData}
          savedReports={savedReports}
          onGenerateReport={generateComplianceReport}
          onViewReport={setViewingReport}
          onBack={handleBack}
        />;
      case 'trends':
        // FT-354a: onBack renders mobile BackButton (returns to dashboard).
        return <TrendsAnalyticsContent
          data={complianceData}
          onBack={handleBack}
        />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Review</h1>
        <p className="mt-1 text-sm text-gray-500">System reviews, compliance reporting, and analytics</p>
      </div>

      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-6">
        {renderTabContent()}
      </div>

      {/* Add/Edit Review Modal */}
      {(showAddReviewModal || editingReview) && (
        <SMSReviewModal
          review={editingReview}
          onSave={handleSaveReview}
          onClose={() => {
            setShowAddReviewModal(false);
            setEditingReview(null);
          }}
        />
      )}

      {/* Report View Modal */}
      {viewingReport && (
        <ReportViewModal
          report={viewingReport}
          onClose={() => setViewingReport(null)}
        />
      )}
    </div>
  );
};

export default ReportsModule;